import * as yup from 'yup';
import { ChangeEventHandler, FC, memo, useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from 'react-query';
import { InputField } from '@studenten/ui-components';

import { authApi } from 'api_entities/auth';
import { AuthError } from 'api_entities/auth/types';
import { APP_ROUTES } from 'constants/routes';
import { CheckBox } from 'src/common/CheckBox';
import { TextLink } from 'src/common/TextLink';
import { Spinner } from 'src/components/Spinner';

import { InputWrapper, CaptionText, SubmitButton, ConditionsWrapper } from './SignUpForm.styles';

const schema = yup
  .object({
    email: yup.string().email().required(),
    password: yup.string().required(),
  })
  .required();

export interface ISignUpForm {
  email: string;
  password: string;
}

export type SignUpFormProps = {
  onSubmit: (values?: ISignUpForm) => void;
  loading?: boolean;
};

const SignUpForm: FC<SignUpFormProps> = ({ onSubmit, loading }) => {
  const router = useRouter();
  const [boxes, setBoxes] = useState<{ terms: boolean; newsletter: boolean }>({
    terms: false,
    newsletter: true,
  });
  const { handleSubmit, formState, register, setError } = useForm<ISignUpForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { email: '', password: '' },
    resolver: yupResolver(schema),
  });

  const { mutate: submitForm, isLoading } = useMutation(authApi.signUp.bind(authApi), {
    onError: (err: AuthError) => {
      if (err?.status === 400 || err?.status === 401) {
        if (err?.data?.errors) {
          Object.entries(err.data.errors).map(([field, message]) => {
            setError(field as 'email' | 'password', { message });
          });
        } else {
          setError('email', { message: err?.data?.message });
        }
      }
    },
    onSuccess: async (_, values) => {
      onSubmit(values);
    },
  });

  const submitHandler = useCallback(
    (values: ISignUpForm) => {
      const from =
        typeof router?.query?.from === 'string' &&
        router.query.from.startsWith(APP_ROUTES.ALL_VACANCIES)
          ? router.query.from
          : undefined;

      submitForm({ ...values, newsletter: boxes.newsletter, ...(from && { redirectAfter: from }) });
    },
    [submitForm]
  );

  const onBoxesChange: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    setBoxes((prev) => ({ ...prev, [target.name]: target.checked }));
  };

  const { errors, isSubmitting } = formState;

  const disableSubmit = loading || isSubmitting || !boxes.terms;

  return (
    <div>
      {isLoading && <Spinner fullScreen />}
      <CaptionText>of via email</CaptionText>
      <form onSubmit={handleSubmit(submitHandler)}>
        <InputWrapper>
          <InputField
            placeholder="Your email"
            {...register('email')}
            error={errors.email?.message}
            withErrorMessage={!!errors.email}
            touched={!!errors.email}
          />
        </InputWrapper>
        <InputWrapper>
          <InputField
            type="password"
            placeholder="Create password"
            {...register('password')}
            error={errors.password?.message}
            withErrorMessage={!!errors.password}
            touched={!!errors.password}
          />
        </InputWrapper>
        <ConditionsWrapper>
          <CheckBox
            primary
            name="newsletter"
            label="Ontvang de leukste banen en nieuws"
            checked={boxes.newsletter}
            onChange={onBoxesChange}
          />
          <CheckBox
            primary
            name="terms"
            label={
              <>
                Ik ga akkoord, met de{' '}
                <TextLink underline href={APP_ROUTES.TERMS_OF_USE} target="_blank">
                  Algemene voorwaarden
                </TextLink>{' '}
                van de website
              </>
            }
            checked={boxes.terms}
            onChange={onBoxesChange}
          />
        </ConditionsWrapper>
        <SubmitButton type="submit" disabled={disableSubmit}>
          Account Aanmaken
        </SubmitButton>
      </form>
    </div>
  );
};

export default memo(SignUpForm);
